import type { FC } from "react";

import { useEffect, useState } from "react";

import { Button, Separator, Spinner } from "@alaffia-technology-solutions/ds";
import { useUserAgent } from "@alaffia-technology-solutions/hooks";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { ChevronDown, ChevronUp } from "lucide-react";

import { SearchResult } from "./search-result";

interface SearchResultsProps {
  query: string;
  results: {
    id: string;
    confidence: number;
    fileId: string;
    pageNumber: number;
    textMatched: string;
  }[];
  onResultPick?: (id?: string) => void;
  isLoading?: boolean;
  className?: string;
}
export const SearchResults: FC<SearchResultsProps> = ({
  query,
  results,
  onResultPick,
  isLoading,
  className,
}) => {
  const [index, setIndex] = useState<number | null>(null);
  const userAgent = useUserAgent();

  const changeIndex = (newIndex: number) => {
    setIndex(newIndex);
    onResultPick?.(results?.[newIndex]?.id);
  };

  const decrementIndex = () => {
    if (!index) {
      changeIndex(results.length - 1);
    } else {
      changeIndex(index - 1);
    }
  };

  const incrementIndex = () => {
    if (index === null || index === results.length - 1) {
      changeIndex(0);
    } else {
      changeIndex(index + 1);
    }
  };

  useEffect(() => {
    setIndex(null);
  }, [query]);

  const wrapperClassName = cn(
    "af-h-full af-w-full af-bg-background af-p-4",
    className,
  );

  if (isLoading) {
    return (
      <div className={wrapperClassName}>
        <Spinner className="af-mx-auto af-mt-2 af-block" />
      </div>
    );
  }

  if (query.length === 0) {
    return (
      <p className="af-p-4 af-text-xs">
        {userAgent.isDesktop ? "Click" : "Tap"} the magnifying glass icon
        {userAgent.isDesktop && (
          <>
            {" "}
            or press{" "}
            <kbd className="af-pointer-events-none af-inline-flex af-h-5 af-select-none af-items-center af-gap-1 af-rounded af-border af-bg-muted af-px-1.5 af-font-mono">
              <span className="af-text-xs">
                {userAgent.os?.name === "macOS" ? "⌘" : "Ctrl"}
              </span>
              /
            </kbd>{" "}
          </>
        )}
        to search
      </p>
    );
  }

  if (results.length === 0)
    return (
      <div className={wrapperClassName}>
        <p className="af-text-xs">No results for: &quot;{query}&quot;</p>
      </div>
    );

  return (
    <div className={wrapperClassName}>
      <div className="af-flex af-items-center af-justify-between">
        <span className="af-py-2 af-text-xs af-text-foreground">
          Search results for: &quot;{query}&quot;
        </span>
        <div className="af-flex">
          <Button
            size="icon"
            className="af-h-8 af-w-8"
            variant="ghost"
            onClick={incrementIndex}
          >
            <ChevronDown size={16} />
          </Button>
          <Button
            size="icon"
            className="af-h-8 af-w-8"
            variant="ghost"
            onClick={decrementIndex}
          >
            <ChevronUp size={16} />
          </Button>
        </div>
      </div>

      <Separator />

      <div className="af-h-full af-overflow-auto af-pb-3">
        {results.map((result, idx) => (
          <SearchResult
            key={result.id}
            {...result}
            query={query}
            active={idx === index}
            onClick={() => changeIndex(idx)}
          />
        ))}
      </div>
    </div>
  );
};
